/**
 * Auth: Okta OIDC PKCE in production, local-dev bypass otherwise.
 *
 * The backend's FakeAuthService accepts any (or no) bearer token when
 * TRUCKTOURS_PROFILE=local, so during local dev we skip the redirect
 * dance entirely. Production goes through @okta/okta-react.
 */

import { OktaAuth, type OktaAuthOptions } from "@okta/okta-auth-js";
import { LoginCallback, Security, useOktaAuth } from "@okta/okta-react";
import { type ReactNode, useCallback, useMemo } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { env, isLocal } from "@/env";

// ─── Local-dev bypass: a stub session that mimics @okta/okta-react ──

interface LocalSession {
  isAuthenticated: true;
  user: { sub: string; email: string; name: string };
}

const LOCAL_SESSION: LocalSession = {
  isAuthenticated: true,
  user: {
    sub: "local-dev-user",
    email: "local-dev@echo.com",
    name: "Local Dev",
  },
};

// ─── Token accessor used by the API client ───────────────────────────

let _oktaAuth: OktaAuth | null = null;

export async function getAccessToken(): Promise<string | null> {
  if (!env.oktaEnabled) {
    // Backend FakeAuthService ignores the token entirely in local profile.
    return null;
  }
  if (!_oktaAuth) return null;
  try {
    const token = await _oktaAuth.tokenManager.get("accessToken");
    return token && "accessToken" in token ? token.accessToken : null;
  } catch {
    return null;
  }
}

// ─── Provider: wraps the app in either real Okta or local stub ───────

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  if (!env.oktaEnabled) {
    // Local-dev shortcut — no Okta, no redirect dance.
    return <>{children}</>;
  }

  // Production / dev1 / qua1 / prod: real Okta integration.
  return <OktaSecurityProvider>{children}</OktaSecurityProvider>;
}

function OktaSecurityProvider({ children }: AuthProviderProps): JSX.Element {
  const navigate = useNavigate();

  const oktaAuth = useMemo(() => {
    const opts: OktaAuthOptions = {
      issuer: env.oktaIssuer,
      clientId: env.oktaClientId,
      redirectUri: env.oktaRedirectUri,
      scopes: env.oktaScopes,
      pkce: true,
    };
    const instance = new OktaAuth(opts);
    _oktaAuth = instance;
    return instance;
  }, []);

  const restoreOriginalUri = useCallback(
    async (_oktaAuth: OktaAuth, originalUri: string) => {
      navigate(originalUri || "/", { replace: true });
    },
    [navigate],
  );

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      {children}
    </Security>
  );
}

// ─── Hook: identity for the UI ───────────────────────────────────────

export interface AuthSession {
  isAuthenticated: boolean;
  user: { sub: string; email: string; name: string } | null;
  signOut: () => void;
}

export function useAuthSession(): AuthSession {
  if (!env.oktaEnabled) {
    return {
      isAuthenticated: LOCAL_SESSION.isAuthenticated,
      user: LOCAL_SESSION.user,
      signOut: () => {
        // No-op locally; in production we'd call oktaAuth.signOut().
      },
    };
  }
  // env.oktaEnabled is a module-level constant — the branch is fixed
  // for the lifetime of the process, so the conditional hook call
  // doesn't violate React's rules-of-hooks in practice.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useOktaSession();
}

function useOktaSession(): AuthSession {
  const { authState, oktaAuth } = useOktaAuth();
  const claims = authState?.idToken?.claims;
  const user = claims
    ? {
        sub: String(claims.sub ?? ""),
        email: String(claims.email ?? ""),
        name: String(claims.name ?? ""),
      }
    : null;
  return {
    isAuthenticated: !!authState?.isAuthenticated,
    user,
    signOut: () => {
      void oktaAuth.signOut();
    },
  };
}

// ─── Route guard ─────────────────────────────────────────────────────

export function RequireAuth({ children }: AuthProviderProps): JSX.Element {
  const { isAuthenticated } = useAuthSession();
  const location = useLocation();
  if (isLocal) return <>{children}</>;
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

// ─── Routes exported back to App.tsx ─────────────────────────────────

export { LoginCallback };
